// A command palette: the query field as the header, a filter bar, grouped results and a key hint
// footer, separated by hairlines rather than nested boxes.
.content {
  gap: 0 !important;
  padding: 0 !important;
  overflow: hidden;
  width: min(calc(100vw - 3rem), 44rem) !important;
  max-height: min(40rem, calc(100dvh - 6rem)) !important;
  font-family: var(--font-body-family), sans-serif;
}

.searchField {
  position: relative;
  display: flex;
  flex: none;
  align-items: center;
  border-bottom: 1px solid var(--color-border);
}

// The field is the dialog's header: the caret marks focus, so it drops the control's box and ring.
.searchInput {
  width: 100%;
  min-height: 3.5rem !important;
  padding-inline: 3rem 4rem !important;
  border: 0 !important;
  border-radius: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
  font-size: 1rem !important;

  &:focus,
  &:focus-visible {
    outline: none !important;
  }
}

.searchIcon,
.escapeKey {
  position: absolute;
  display: inline-flex;
  color: var(--color-muted-foreground);
  pointer-events: none;
}

.searchIcon {
  inset-inline-start: 1.125rem;
}

.escapeKey {
  inset-inline-end: 1rem;
}

.toolbar {
  display: flex;
  flex: none;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1rem;
  border-bottom: 1px solid var(--color-border);
}

.sdkFilter {
  width: 9.5rem;
  margin-inline-start: auto;
}

.alerts {
  display: flex;
  flex: none;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.75rem 1rem 0;
}

.results {
  flex: 1 1 auto;
  min-height: 18rem;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 0.25rem 0.5rem 0.5rem;
  scroll-padding-block: 2.25rem 0.5rem;
}

.group + .group {
  margin-top: 0.25rem;
}

.groupHeader {
  position: sticky;
  top: 0;
  z-index: 1;
  display: flex;
  align-items: baseline;
  gap: 0.75rem;
  padding: 0.625rem 0.625rem 0.375rem;
  background: var(--color-card);
}

.groupNote {
  overflow: hidden;
  margin-inline-start: auto;
  color: var(--color-muted);
  font-size: 0.75rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.result {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  min-height: 2.75rem;
  padding: 0.5rem 0.625rem;
  border: 0;
  border-radius: var(--radius-md);
  background: transparent;
  color: var(--color-foreground);
  font-family: var(--font-body-family), sans-serif;
  text-align: start;
  cursor: pointer;

  &[data-active] {
    background: var(--color-card-hover);
  }
}

.resultContent {
  display: flex;
  flex: 1;
  min-width: 0;
  flex-direction: column;
  gap: 0.125rem;
}

.resultHeading {
  display: flex;
  min-width: 0;
  align-items: baseline;
  gap: 0.5rem;
}

.resultTitle {
  overflow: hidden;
  flex: 0 1 auto;
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.35;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.resultPage {
  overflow: hidden;
  flex: 0 10 auto;
  color: var(--color-muted);
  font-size: 0.8125rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.resultDetail {
  overflow: hidden;
  color: var(--color-muted);
  font-size: 0.8125rem;
  line-height: 1.4;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.result mark {
  background: none;
  color: var(--color-foreground);
  font-weight: 600;
  text-decoration: underline;
  text-decoration-color: color-mix(in srgb, var(--color-primary) 55%, transparent);
  text-decoration-thickness: 2px;
  text-underline-offset: 2px;
}

.resultTitle mark {
  color: var(--color-primary);
  text-decoration: none;
}

.status {
  flex: none;
}

.enterKey {
  flex: none;
  visibility: hidden;
}

.result[data-active] .enterKey {
  visibility: visible;
}

.state {
  min-height: 18rem;
}

.footer {
  display: flex;
  flex: none;
  align-items: center;
  gap: 1rem;
  min-height: 2.5rem;
  padding: 0 1rem;
  border-top: 1px solid var(--color-border);
  color: var(--color-muted);
  font-size: 0.75rem;
}

.footer > span {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
}

.footerHint {
  margin-inline-start: auto;
}

@media (max-width: 639px) {
  .content {
    width: calc(100vw - 1rem) !important;
    max-width: calc(100vw - 1rem) !important;
    max-height: calc(100dvh - 1rem) !important;
  }

  .searchInput {
    padding-inline-end: 1rem !important;
  }

  .toolbar {
    padding-inline: 0.75rem;
  }

  .sdkFilter {
    width: 100%;
    margin-inline-start: 0;
  }

  .escapeKey,
  .status,
  .enterKey,
  .footer {
    display: none !important;
  }

  .results,
  .state {
    min-height: min(24rem, calc(100dvh - 10rem));
  }

  .results {
    padding-inline: 0.25rem;
  }

  .groupNote {
    white-space: normal;
  }
}
